import React, { useEffect, useRef, useState } from 'react';
import SignedVideo from './SignedVideo';

type Props = {
  src: string;
  poster?: string | null;
  style?: React.CSSProperties;
};

/** Muted preview that plays while mostly on screen and pauses otherwise. */
const AutoplayPreview: React.FC<Props> = ({ src, poster, style }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          el.muted = true;
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: [0, 0.6, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SignedVideo
        ref={ref}
        src={src}
        poster={poster || undefined}
        muted
        loop
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        style={style}
      />
      {!playing && (
        <div style={playIcon} aria-hidden>
          <span style={{ marginLeft: 3 }}>▶</span>
        </div>
      )}
    </>
  );
};

const playIcon: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.45)',
  backdropFilter: 'blur(6px)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  pointerEvents: 'none',
  border: '1.5px solid rgba(255,255,255,0.7)',
};

export default AutoplayPreview;
